<script>
import { mapGetters } from 'vuex';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import { emitter } from 'shared/helpers/mitt';
import SidepanelSwitch from 'dashboard/components-next/Conversation/SidepanelSwitch.vue';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    CmdBarConversationSnooze,
    SidepanelSwitch,
    ConversationSidebar,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
  },
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
    };
  },
  data() {
    return {
      showSearchModal: false,

      // ✅ debounce timer para evitar spam
      waReadHookTimer: null,

      // ✅ dedupe: evita duplicidade em poucos segundos
      lastWaReadKey: null,
      lastWaReadAt: 0,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    isOnExpandedLayout() {
      const {
        LAYOUT_TYPES: { CONDENSED },
      } = wootConstants;
      const { conversation_display_type: conversationDisplayType = CONDENSED } =
        this.uiSettings;
      return conversationDisplayType !== CONDENSED;
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },
  },

  watch: {
    // ✅ 1) sempre que troca a rota (clicou em outra conversa), busca e agenda 1 webhook
    conversationId(newId, oldId) {
      this.fetchConversationIfUnavailable();

      const n = Number(newId || 0);
      const o = Number(oldId || 0);
      if (n && n !== o) {
        this.scheduleWhatsappReadWebhook('open');
      }
    },

    // ✅ 2) se chegou msg nova enquanto a conversa está aberta, agenda webhook
    'currentChat.unread_count'(newVal, oldVal) {
      if (!this.conversationId) return;

      const routeId = parseInt(this.conversationId, 10);
      if (Number(this.currentChat?.id) !== routeId) return;

      const n = Number(newVal || 0);
      const o = Number(oldVal || 0);

      // Só dispara quando realmente aumentou (chegou msg nova)
      if (n > o) {
        this.scheduleWhatsappReadWebhook('incoming');
      }
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });

    window.addEventListener('keydown', this.handleKeyAssignShortcut);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyAssignShortcut);
    clearTimeout(this.waReadHookTimer);
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },

    // ✅ debounce + dedupe (evita 2x/3x)
    scheduleWhatsappReadWebhook(reason = 'unknown') {
      const chat = this.currentChat;
      if (!chat?.id) return;

      const key = `${chat.id}:${reason}`;
      const now = Date.now();

      // trava por 3s para o mesmo motivo na mesma conversa
      if (this.lastWaReadKey === key && now - this.lastWaReadAt < 3000) {
        return;
      }

      clearTimeout(this.waReadHookTimer);
      this.waReadHookTimer = setTimeout(() => {
        this.lastWaReadKey = key;
        this.lastWaReadAt = Date.now();
        this.triggerWhatsappReadWebhook(reason);
      }, 250);
    },

    // ✅ dispara 1 POST controlado
    async triggerWhatsappReadWebhook(reason = 'unknown') {
      try {
        const chat = this.currentChat;
        if (!chat?.id) return;

        await fetch(
          'https://n8n1.artificialseller.com/webhook/whatsapp-mark-read-by-conversation',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              account_id: this.accountId,
              conversation_id: chat.id,
              inbox_id: chat.inbox_id,
              reason, // ajuda debug no n8n/log
            }),
          }
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('WA read webhook failed', e);
      }
    },

    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            // ✅ ao abrir: agenda webhook 1x (controlado)
            this.scheduleWhatsappReadWebhook('open');

            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
    handleKeyAssignShortcut(e) {
      if (e.altKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        this.assignToSelf();
      }
    },
    assignToSelf() {
      const chat = this.currentChat;
      const user = this.$store.getters['getCurrentUser'];

      if (!chat?.id || !user?.id) return;

      this.$store.dispatch('assignAgent', {
        conversationId: chat.id,
        agentId: user.id,
      });
    },
  },
};
</script>

<template>
  <section class="flex w-full h-full min-w-0">
    <ChatList
      :show-conversation-list="showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <ConversationBox
      v-if="showMessageView"
      :inbox-id="inboxId"
      :is-on-expanded-layout="isOnExpandedLayout"
    >
      <SidepanelSwitch v-if="currentChat.id" />
    </ConversationBox>
    <ConversationSidebar v-if="shouldShowSidebar" :current-chat="currentChat" />
    <CmdBarConversationSnooze />
  </section>
</template>
